// NOTICE: KAPTION-INTEGRATION
//
// Availability presentation for the command bar's "Assign an agent" list.
//
// WHY: the sidebar assignee dropdown shows each agent's presence (the dot on
// their avatar), but the command bar dropped it — every agent rendered with the
// same generic person icon. Agents pick from that list all day, so conversations
// kept landing on people who were not connected, with nothing on screen to warn
// them. The data was always there (`agent.availability_status`, and the list is
// already sorted online → busy → offline by getSortedAgentsByAvailability); the
// command bar simply never rendered it.
//
// The icons are raw HTML strings because ninja-keys renders them with lit's
// unsafeHTML inside a SHADOW ROOT — Tailwind classes do not cross that boundary.
// CSS custom properties DO, so the colours come from the same `--teal-10` /
// `--amber-10` / `--slate-10` variables the Avatar component uses, which means
// light/dark mode keeps working for free.
//
// Colour is not the only cue: `online` and `busy` are filled discs, `offline` is
// a hollow ring, and the status word is appended to the title. That matters for
// colour-blind users and, more mundanely, for anyone skimming the list fast.

const STATUS_COLOR_VAR = {
  online: '--teal-10', // same token as Avatar's bg-n-teal-10
  busy: '--amber-10',
  offline: '--slate-10',
};

const I18N_STATUS_PREFIX = 'PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS';

const svgWrapper = inner =>
  `<svg role="img" aria-hidden="true" class="ninja-icon ninja-icon--fluent" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">${inner}</svg>`;

/**
 * The presence dot for an agent, as an HTML string ninja-keys can render.
 *
 * Filled disc for online/busy, hollow ring for offline — shape carries the same
 * information as colour so the cue survives a colour-blind reader.
 *
 * @param {string} status - 'online' | 'busy' | 'offline'
 * @returns {string|null} SVG markup, or null when the status is unknown (the
 *   caller then keeps the generic agent icon — e.g. the "None" pseudo-agent,
 *   which has no presence at all).
 */
export const availabilityIcon = status => {
  const colorVar = STATUS_COLOR_VAR[status];
  if (!colorVar) return null;

  const color = `rgb(var(${colorVar}))`;
  const inner =
    status === 'offline'
      ? `<circle cx="9" cy="9" r="3.5" stroke="${color}" stroke-width="1.5" />`
      : `<circle cx="9" cy="9" r="4.5" fill="${color}" />`;

  return svgWrapper(inner);
};

/**
 * Localised status word ("En línea" / "Ocupado" / "Desconectado"), reusing the
 * keys the profile menu already ships translated — no new i18n strings.
 *
 * @param {string} status
 * @param {Function} t - vue-i18n translate
 * @returns {string} the label, or '' when the status is unknown
 */
export const availabilityLabel = (status, t) =>
  STATUS_COLOR_VAR[status]
    ? t(`${I18N_STATUS_PREFIX}.${status.toUpperCase()}`)
    : '';

/**
 * Title for an agent row: "Ramón Romero · Desconectado".
 *
 * ninja-keys renders the title as plain text, so this is the only place the
 * status can be stated in words rather than implied by a colour.
 *
 * @param {Object} agent
 * @param {Function} t
 * @returns {string}
 */
export const agentActionTitle = (agent, t) => {
  const label = availabilityLabel(agent?.availability_status, t);
  return label ? `${agent.name} · ${label}` : (agent?.name ?? '');
};

/**
 * Search keywords so typing "desconectado" (or "offline", whatever the UI
 * language) narrows the list to those agents. ninja-keys matches title OR
 * keywords, and the raw English key is included so it works regardless of the
 * agent's chosen locale.
 *
 * @param {Object} agent
 * @param {Function} t
 * @returns {string}
 */
export const agentActionKeywords = (agent, t) => {
  const status = agent?.availability_status;
  if (!STATUS_COLOR_VAR[status]) return '';
  return [status, availabilityLabel(status, t)].filter(Boolean).join(' ');
};
